import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import InputField from "../components/textfields/InputField";
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { loadPincodeDb } from '../utils/pincodeDb';
import { API } from '../utils/apiUrls';
import { deepTrimStrings } from '../utils/helpers';
import PickupAddressModal, { deletePickupAddress, DeleteConfirmModal } from '../components/PickupAddressModal';
import { useOnboardingGate } from '../components/onboarding/OnboardingGate';
import OrderCreationIssueModal from '../components/orders/OrderCreationIssueModal';
import { interpretOrderCreationError, interpretOrderCreationResponse } from '../utils/orderCreationError';

// Enhanced Icons Component
const Icons = {
  User: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Location: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  Package: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  Truck: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  ),
  Receipt: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Check: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  Phone: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  ),
  Mail: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  MapPin: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    </svg>
  ),
  Sparkles: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  ),
  Lightning: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  Close: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Search: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Refresh: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  RotateBack: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
    </svg>
  ),
};

const CreateReverseOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requireBusinessOps } = useOnboardingGate();
  const [draftOrderId, setDraftOrderId] = useState(location?.state?.draftOrderId || null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSuccessModal, setDraftSuccessModal] = useState({ open: false, draftId: null, isUpdate: false });
  const [orderIssue, setOrderIssue] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [loadingPincode, setLoadingPincode] = useState(false);
  const [courierServiceability, setCourierServiceability] = useState([]);
  const [customerLookupResults, setCustomerLookupResults] = useState([])
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [awbNumber, setAwbNumber] = useState("");

  const [formData, setFormData] = useState({
    pickupDetails: {
      name: "",
      phoneNumber: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
    },
    orderDetails: {
      clientOrderId: "",
      invoiceValue: 0,
      itemDetails: [
        {
          name: "",
          quantity: 1,
          unitPrice: "",
          skuId: "",
        },
      ]
    },
    packageDetails: {
      weight: "",
      length: "",
      breadth: "",
      height: "",
    },
    courierDetails: {
      courierPartner: "",
      useRecommendation: true,
    },
    paymentMode: "Prepaid",
  });

  const [dropDetails, setDropDetails] = useState([]);
  const [selectedDrop, setSelectedDrop] = useState(null);
  const [showHSNDetails, setShowHSNDetails] = useState(false);
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [courierOptions, setCourierOptions] = useState([]);
  const [loadingCouriers, setLoadingCouriers] = useState(false);
  const [errors, setErrors] = useState({});
  const [units, setUnits] = useState({ weight: 'gm', length: 'cm', breadth: 'cm', height: 'cm' });
  const cloneFrom = location?.state?.cloneFrom;
  const cloneMode = Boolean(cloneFrom);
  const [highestStepReached, setHighestStepReached] = useState(1);
  const [expandedHSNItems, setExpandedHSNItems] = useState({});

  // Address management state
  const [addrModalOpen, setAddrModalOpen] = useState(false);
  const [addrModalMode, setAddrModalMode] = useState("add");
  const [addrModalLocation, setAddrModalLocation] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pickupToDelete, setPickupToDelete] = useState(null);
  const [deletingPickup, setDeletingPickup] = useState(false);

  // Pre-fill form when cloning from an existing RVP order
  useEffect(() => {
    if (!cloneFrom) return;

    const allItems = (cloneFrom.shipment_packages || []).flatMap(
      (pkg) => (pkg.items || []).map((item) => ({
        name: item.item_general_details?.item_name || "",
        skuId: item.item_general_details?.sku || "",
        quantity: item.quantity || 1,
        unitPrice: item.item_payment_details?.unit_price || "",
      }))
    );
    const itemDetails = allItems.length > 0
      ? allItems
      : [{ name: "", skuId: "", quantity: 1, unitPrice: "" }];

    const packageDims = cloneFrom.shipment_package_dimensions || {};
    const dimensionUnit = packageDims.unit || "cm";

    const baseRef = cloneFrom.order_reference_number || "";
    const clientOrderId = baseRef.replace(/(_CLONE|_retry)$/i, "") + "_CLONE";

    const paymentMode = (cloneFrom.payment_details?.payment_mode || "").toUpperCase() === "COD"
      ? "COD"
      : "Prepaid";

    setFormData((prev) => ({
      ...prev,
      pickupDetails: {
        name: cloneFrom.pickup_name || "",
        phoneNumber: cloneFrom.pickup_poc_contact_number || "",
        email: cloneFrom.pickup_poc_email || "",
        address: cloneFrom.pickup_address || "",
        city: cloneFrom.pickup_city || "",
        state: cloneFrom.pickup_state || "",
        pincode: cloneFrom.pickup_pincode || "",
      },
      orderDetails: {
        ...prev.orderDetails,
        clientOrderId,
        invoiceValue: cloneFrom.payment_details?.total_amount?.toString() || "",
        invoiceNumber: cloneFrom.invoice_number || "",
        codAmount: paymentMode === "COD" ? (cloneFrom.payment_details?.total_amount?.toString() || "") : undefined,
        itemDetails,
      },
      packageDetails: {
        weight: cloneFrom.dead_weight_in_grams?.toString() || "",
        length: packageDims.length ? String(packageDims.length) : "",
        breadth: packageDims.breadth ? String(packageDims.breadth) : "",
        height: packageDims.height ? String(packageDims.height) : "",
      },
      paymentMode,
    }));

    setUnits({ weight: "gm", length: dimensionUnit, breadth: dimensionUnit, height: dimensionUnit });
  }, []);

  // AWB lookup
  const searchOrder = async () => {
    setLoading(true);
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    try {
      const apiSearchOrder = API.searchOrders();

      const resp = await axios.post(
        apiSearchOrder,
        { merchant_id: merchantId, awb_number: awbNumber || null, limit: 1, skip: 0, sort_field: "shipment_order_date_time_utc", sort_direction: "DESC" },
        { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
      );

      const order = resp.data?.orders?.[0];
      if (!order) { toast.error("Order not found."); return; }

      // Map item details
      const item = order.shipment_packages?.[0]?.items?.[0];
      const itemDetails = item ? [{
        name: item.item_general_details?.item_name || "",
        skuId: item.item_general_details?.sku || "",
        quantity: item.quantity || 1,
        unitPrice: item.item_payment_details?.unit_price || ""
      }] : [{ name: "", skuId: "", quantity: 1, unitPrice: "" }];

      // Build pickup and drop from order
      const pickupData = {
        name: order.drop_name || "", phoneNumber: order.drop_poc_contact_number || "",
        email: "", address: order.drop_address || "", city: order.drop_city || "",
        state: order.drop_state || "", pincode: order.drop_pincode || ""
      };
      const dropData = {
        pickup_poc: order.pickup_name || "", pickup_poc_contact_number: order.pickup_poc_contact_number || "",
        pickup_poc_email: order.pickup_poc_email || "", pickup_address: order.pickup_address || "",
        pickup_city: order.pickup_city || "", pickup_state: order.pickup_state || "",
        pickup_postal_code: order.pickup_pincode || ""
      };

      // Extract package dimensions from shipment_package_dimensions if available
      const packageDims = order.shipment_package_dimensions || {};
      const packageDetails = {
        weight: order.dead_weight_in_grams?.toString() || "",
        length: packageDims.length ? String(packageDims.length) : "",
        breadth: packageDims.breadth ? String(packageDims.breadth) : "",
        height: packageDims.height ? String(packageDims.height) : "",
      };

      // Extract units from shipment_package_dimensions if available
      const dimensionUnit = packageDims.unit || 'cm';
      const newUnits = {
        weight: 'gm',
        length: dimensionUnit,
        breadth: dimensionUnit,
        height: dimensionUnit,
      };

      // Populate formData and drop state; fill Client Order ID from order_reference_number + _REVERSE
      const clientOrderIdFromSearch = (order.order_reference_number || "") + "_REVERSE";
      setFormData(prev => ({
        ...prev,
        pickupDetails: pickupData,
        orderDetails: { ...prev.orderDetails,
          clientOrderId: clientOrderIdFromSearch,
          invoiceValue: order.payment_details?.total_amount?.toString() || "",
          invoiceNumber: order.invoice_number || "",
          codAmount: undefined,
          itemDetails
        },
        paymentMode: "Prepaid",
        packageDetails
      }));

      setUnits(newUnits);

      setDropDetails([dropData]);
      setSelectedDrop(dropData);

      toast.success("Fetched order details");
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch order");
    } finally {
      setLoading(false);
    }
  };

  // ---Gen-ID helper ---
  const handleGenerateOrderId = () => {
    setFormData((f) => ({
      ...f,
      orderDetails: {
        ...f.orderDetails,
        clientOrderId: uuidv4(),
      },
    }));
  };

  // --- fetchSKUDetails populates name/price/quantity + package dims ---
  const fetchSKUDetails = async (index, sku) => {
    if (!sku) return;
    try {
      const authToken = localStorage.getItem('authToken');
      const merchantId = localStorage.getItem('merchantId');
      const apiGetSkuDetails = API.getProductSku(merchantId, sku);
      const response = await axios.get(apiGetSkuDetails, {
        headers: { Authorization: authToken },
        validateStatus: () => true,
      });
      const data = typeof response.data === 'object' && response.data !== null ? response.data : null;
      if (!data || Object.keys(data).length === 0) return; // ignore blank/bad responses
  
      setFormData((prev) => {
        const items = [...prev.orderDetails.itemDetails];
        items[index] = {
          ...items[index],
          name: data.product_name || items[index].name,
          unitPrice: data.item_pricing_value.unit_price || items[index].unitPrice,
          quantity: "1",
          skuId: items[index].skuId,
        };
        return {
          ...prev,
          orderDetails: { ...prev.orderDetails, itemDetails: items },
          packageDetails: {
            weight: data.product_weight.product_weight,
            length: data.product_dimensions.product_length,
            breadth: data.product_dimensions.product_width,
            height: data.product_dimensions.product_height,
          },
          units: {
            weight: data.product_weight.unit,
            length: data.product_dimensions.unit,
            breadth: data.product_dimensions.unit,
            height: data.product_dimensions.unit,
          },
        };
      });
    } catch (err) {
      console.error('Failed to fetch SKU details', err);
      toast.error('Unable to fetch product details');
    }
  };


  // Conversion functions for payload only
  const convertLength = (value, unit) => {
    const num = parseFloat(value) || 0;
    return unit === 'inch' ? num * 2.54 : num;
  };
  const convertWeight = (value, unit) => {
    const num = parseFloat(value) || 0;
    return unit === 'kg' ? num * 1000 : num;
  };

  const handlePackageValueChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      packageDetails: { ...prev.packageDetails, [field]: value }
    }));
  };
  const handlePackageUnitChange = (field, unit) => {
    setUnits(prev => ({ ...prev, [field]: unit }));
  };

  const handlePincodeChange = async (e) => {
    const pin = e.target.value.trim();
    setFormData(prev => ({
      ...prev,
      pickupDetails: { ...prev.pickupDetails, pincode: pin }
    }));

    if (!/^\d{6}$/.test(pin)) return;

    setLoadingPincode(true);
    const db = await loadPincodeDb();
    const [entry] = db ? db.search(pin) : [];
    if (entry) {
      setFormData(prev => ({
        ...prev,
        pickupDetails: {
          ...prev.pickupDetails,
          city: entry.city,
          state: entry.state
        }
      }));
      setLoadingPincode(false);
      return;
    }
    try {
      const response = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);
      const data = response.data?.[0];
      if (data?.Status === 'Success' && data.PostOffice?.length) {
        const postOffice = data.PostOffice[0];
        setFormData(prev => ({
          ...prev,
          pickupDetails: {
            ...prev.pickupDetails,
            city: postOffice.District,
            state: postOffice.State
          }
        }));
      } else {
        throw new Error('No Postal data');
      }
    } catch (err) {
      toast.error('Invalid PIN Code');
    } finally {
      setLoadingPincode(false);
    }
  };



  useEffect(() => {
    if (step === 1) {
      fetchAndStoreDropDetails();
    }
  }, [step]);

  const fetchAndStoreDropDetails = async (skipAutoSelect = false) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      console.error("🚨 Auth token not found.");
      return [];
    }

    const apiGetPickupAddress = API.getPickupDetails(localStorage.getItem("parentClientId"));

    console.log("🔗 Drop Details API URL:", apiGetPickupAddress);

    try {
      const response = await axios.get(apiGetPickupAddress, { headers: { Authorization: authToken } });

      if (response.data && response.data.length > 0) {
        console.log("✅ Drop Locations Fetched:", response.data);
        localStorage.setItem("dropLocations", JSON.stringify(response.data));
        setDropDetails(response.data);
        if (!skipAutoSelect) setSelectedDrop(response.data[0]);
        return response.data;
      } else {
        console.warn("⚠️ No Drop locations available.");
        setDropDetails([]);
        return [];
      }
    } catch (error) {
      console.error("❌ Error fetching Drop locations:", error);
      setDropDetails([]);
      return [];
    }
  };

  useEffect(() => {
    if (step === 2) {
      const tryMatchCloneDrop = (addresses) => {
        // For reverse orders, match the drop (return warehouse) address from cloneFrom
        if (!cloneFrom) return false;
        // In reverse order drafts, the "drop" fields represent the return warehouse
        const clonedPostal = (cloneFrom.drop_pincode || cloneFrom.drop_postal_code || cloneFrom.pickup_pincode || cloneFrom.pickup_postal_code || "").trim();
        const clonedPhone = (cloneFrom.drop_poc_contact_number || cloneFrom.pickup_poc_contact_number || "").trim();
        const clonedAddress = (cloneFrom.drop_address || cloneFrom.pickup_address || "").trim().toLowerCase();

        if (!clonedPostal && !clonedPhone && !clonedAddress) return false;

        const match = addresses.find((addr) => {
          const addrPostal = (addr.pickup_postal_code || "").trim();
          const addrPhone = (addr.pickup_poc_contact_number || "").trim();
          const addrAddress = (addr.pickup_address || "").trim().toLowerCase();

          if (clonedPostal && addrPostal === clonedPostal) {
            if (clonedPhone && addrPhone === clonedPhone) return true;
            if (clonedAddress && addrAddress.includes(clonedAddress.slice(0, 30))) return true;
          }
          return false;
        });

        if (match) {
          setSelectedDrop(match);
          return true;
        }

        // No match - auto-open "Add New Address" modal pre-filled
        const mappedDrop = {
          pickup_address: cloneFrom.drop_address || cloneFrom.pickup_address || "",
          pickup_city: cloneFrom.drop_city || cloneFrom.pickup_city || "",
          pickup_postal_code: cloneFrom.drop_pincode || cloneFrom.drop_postal_code || cloneFrom.pickup_pincode || cloneFrom.pickup_postal_code || "",
          pickup_poc: cloneFrom.drop_name || cloneFrom.drop_poc || cloneFrom.pickup_name || cloneFrom.pickup_poc || "",
          pickup_organisation: cloneFrom.drop_organisation || cloneFrom.pickup_organisation || "",
          pickup_poc_contact_number: cloneFrom.drop_poc_contact_number || cloneFrom.pickup_poc_contact_number || "",
          pickup_poc_email: cloneFrom.drop_poc_email || cloneFrom.pickup_poc_email || "",
          pickup_state: cloneFrom.drop_state || cloneFrom.pickup_state || "",
          pickup_district: cloneFrom.drop_district || cloneFrom.pickup_district || "",
          pickup_country_code: cloneFrom.drop_country_code || cloneFrom.pickup_country_code || "IN",
          pickup_landmark: cloneFrom.drop_landmark || cloneFrom.pickup_landmark || "",
          gst_number: cloneFrom.gst_number || "",
        };
        const hasData = mappedDrop.pickup_postal_code || mappedDrop.pickup_address || mappedDrop.pickup_poc;
        if (hasData) {
          setAddrModalMode("add");
          setAddrModalLocation(mappedDrop);
          setAddrModalOpen(true);
        }
        return false;
      };

      const stored = JSON.parse(localStorage.getItem("dropLocations") || "[]");
      if (Array.isArray(stored) && stored.length > 0) {
        setDropDetails(stored);
        if (!tryMatchCloneDrop(stored)) {
          setSelectedDrop(stored[0]);
        }
      } else {
        fetchAndStoreDropDetails(true).then((fetched) => {
          if (fetched && fetched.length > 0) {
            if (!tryMatchCloneDrop(fetched)) {
              setSelectedDrop(fetched[0]);
            }
          } else if (cloneFrom) {
            tryMatchCloneDrop([]);
          }
        });
      }
    }
  }, [step]);

  /* ── Address management handlers ── */
  const openAddAddress = () => { setAddrModalMode("add"); setAddrModalLocation(null); setAddrModalOpen(true); };
  const openEditAddress = (loc) => { setAddrModalMode("edit"); setAddrModalLocation(loc); setAddrModalOpen(true); };
  const handleAddressSaved = () => {
    fetchAndStoreDropDetails().then((fetched) => {
      if (fetched && fetched.length > 0) {
        setSelectedDrop(fetched[fetched.length - 1]);
      }
    });
  };
  const openDeleteAddress = (id) => { setPickupToDelete(id); setDeleteConfirmOpen(true); };
  const confirmDeleteAddress = async () => {
    setDeletingPickup(true);
    await deletePickupAddress(pickupToDelete, () => {
      fetchAndStoreDropDetails();
      if (selectedDrop?.pickup_address_id === pickupToDelete) setSelectedDrop(null);
    });
    setDeletingPickup(false);
    setDeleteConfirmOpen(false);
    setPickupToDelete(null);
  };

  const steps = [
    { key: "pickupDetails", label: "Customer", icon: Icons.User },
    { key: "dropDetails", label: "Drop", icon: Icons.Location },
    { key: "orderDetails", label: "Order", icon: Icons.Receipt },
    { key: "packageDetails", label: "Package", icon: Icons.Package },
    { key: "courierDetails", label: "Courier", icon: Icons.Truck },
    { key: "summary", label: "Success", icon: Icons.Check },
  ];

  const validateStep = () => {
    const currentSection = steps[step - 1].key;
    const newErrors = {};
  
    // 1) If we’re on orderDetails, prune out any optional-but-hidden keys
    let fieldsToCheck = { ...formData[currentSection] };
    if (currentSection === "orderDetails") {
      if (!showShippingDetails) delete fieldsToCheck.invoiceNumber;
      if (!showHSNDetails)     delete fieldsToCheck.hsn;
      if (formData.paymentMode !== "COD") delete fieldsToCheck.codAmount;
    }
  
    // 2) Now run your usual required-field logic over only the remaining keys
    Object.keys(fieldsToCheck).forEach((field) => {
      const value = fieldsToCheck[field];
  
      // skip email always, as before
      if (currentSection === "pickupDetails" && field === "email") return;
  
      // your blank/null check
      if (value === "" || value == null || (typeof value === "string" && !value.trim())) {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
  
      // phone / pincode patterns
      if (field === "phoneNumber" && !/^[0-9]{10}$/.test(value)) {
        newErrors[field] = "Phone Number must be 10 digits";
      }
      if (field === "pincode" && !/^[0-9]{6}$/.test(value)) {
        newErrors[field] = "Pincode must be 6 digits";
      }
    });
  
    // 3) Your existing itemDetails checks
    if (currentSection === "orderDetails") {
      if (!formData.orderDetails.clientOrderId.trim()) {
        newErrors.clientOrderId = "Client OrderID is required";
      }
      if (Number(formData.orderDetails.invoiceValue) <= 0) {
        newErrors.invoiceValue = "Invoice Value is required";
      }
      formData.orderDetails.itemDetails.forEach((item, idx) => {
        if (!item.name.trim())       newErrors[`name-${idx}`] = "Name is required";
        if (!item.skuId.trim())      newErrors[`skuId-${idx}`] = "SKU ID is required";
        if (!String(item.quantity).trim())  newErrors[`quantity-${idx}`] = "Quantity is required";
        if (!String(item.unitPrice).trim()) newErrors[`unitPrice-${idx}`] = "Unit Price is required";
      });
    }
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  

  // Calculate overall form completion percentage (consistent with Forward order)
  const calculateCompletionPercentage = () => {
    if (step === 6) return 100;
    let totalFields = 0;
    let filledFields = 0;
    // Step 1
    const pickup = formData.pickupDetails;
    const pickupFields = [pickup.name, pickup.phoneNumber, pickup.address, pickup.city, pickup.state, pickup.pincode];
    totalFields += pickupFields.length;
    filledFields += pickupFields.filter(v => v && String(v).trim()).length;
    // Step 3
    const order = formData.orderDetails;
    totalFields += 2;
    if (order.clientOrderId?.trim()) filledFields++;
    if (Number(order.invoiceValue) > 0) filledFields++;
    // Step 4
    const pkg = formData.packageDetails;
    const pkgFields = [pkg.weight, pkg.length, pkg.breadth, pkg.height];
    totalFields += pkgFields.length;
    filledFields += pkgFields.filter(v => v && String(v).trim()).length;
    // Step 2 (drop selected)
    totalFields += 1;
    if (selectedDrop) filledFields++;
    return Math.round((filledFields / totalFields) * 100);
  };

  const handleNext = () => {
    if (validateStep()) {
      if (step === 5) {
        handleSubmit();
      } else {
        const nextStep = step === 4 ? 5 : step + 1;
        setStep(nextStep);
        setHighestStepReached(prev => Math.max(prev, nextStep));
        if (step === 4) {
          prefetchCouriersForStep5();
        }
      }
    }
  };

  const prefetchCouriersForStep5 = async () => {
    try {
      setLoadingCouriers(true);
      await Promise.all([
        fetchCourierPricing(),
        fetchServiceability(),
      ]);
    } finally {
      setLoadingCouriers(false);
    }
  };

  const handleBack = () => {
    setStep(prev => (prev > 1 ? prev - 1 : prev));
  };

  const handleStepClick = (targetStep) => {
    if (targetStep <= highestStepReached && targetStep !== step) {
      setStep(targetStep);
    }
  };

  const handleCourierSelection = async (useRecommendation) => {
    setFormData((prev) => ({
      ...prev,
      courierDetails: {
        ...prev.courierDetails,
        useRecommendation,
        courierPartner: useRecommendation ? "" : prev.courierDetails.courierPartner,
      },
    }));
  };

  const handlePhoneNumberChange = async (e) => {
    const phoneNumber = e.target.value.trim()
    setFormData((prev) => ({
      ...prev,
      pickupDetails: { ...prev.pickupDetails, phoneNumber },
    }))

    if (/^\d{10}$/.test(phoneNumber)) {
      try {
        const authToken = localStorage.getItem('authToken')
        const merchantId = localStorage.getItem('merchantId')
        const apiAddressLookupFromMobile = API.addressLookupFromMobile();
        console.log("🔗 Customer Lookup API URL:", apiAddressLookupFromMobile)
        const response = await axios.request({
          method: 'POST',
          url: apiAddressLookupFromMobile,
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json',
          },
          data: {
            phone_number: phoneNumber,
            merchant_id: merchantId,
          },
        })

        if (Array.isArray(response.data) && response.data.length > 0) {
          setCustomerLookupResults(response.data)
          setShowCustomerModal(true)
        }
      } catch (err) {
        console.error('Customer lookup failed', err)
      }
    }
  }

  const selectCustomer = (cust) => {
    setFormData((prev) => ({
      ...prev,
      pickupDetails: {
        ...prev.pickupDetails,
        name: cust.drop_poc || '',
        phoneNumber: cust.drop_poc_contact_number || prev.pickupDetails.phoneNumber,
        email: cust.drop_poc_email || prev.pickupDetails.email,
        address: cust.drop_address || '',
        city: cust.drop_city || '',
        state: cust.drop_state || '',
        pincode: cust.drop_postal_code || '',
      },
    }))
    setShowCustomerModal(false)
  }

  const fetchCourierPricing = async () => {
    setLoadingCouriers(true);
    setCourierOptions([]);
    try {
      const allocatedCarriersRaw = JSON.parse(localStorage.getItem("allocatedCarriers") || "[]");
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      const apiFetchCourierPricing = API.merchantPricingBatch();

      if (!authToken) {
        toast.error("Missing authentication.");
        setLoadingCouriers(false);
        return;
      }

      // Reverse order: send only REVERSE carrier partners in the request payload
      const reverseMatcher = /(_B2CREVERSE_|REVERSE_)/i; // matches “…_B2CREVERSE_…” or “…REVERSE_…”
      const reverseCarriers = allocatedCarriersRaw
        .map((c) => (typeof c === "string" ? { carrier_partner_id: c } : c))
        .filter(
          (c) =>
            c &&
            typeof c.carrier_partner_id === "string" &&
            reverseMatcher.test(c.carrier_partner_id)
        );

      if (!reverseCarriers.length) {
        toast.info("No reverse-enabled carriers allocated.");
        setLoadingCouriers(false);
        return;
      }

      const requestPayload = reverseCarriers.map((carrier) => ({
        correlation_id: uuidv4(),
        carrier_partner_id: carrier.carrier_partner_id,
        shipment_type: "RVP",
        payment_mode: "PREPAID",
        invoice_value: parseFloat(formData.orderDetails.invoiceValue),
        rvp_with_quality_check: false,
        weight_in_grams: convertWeight(formData.packageDetails.weight, units.weight),
        length_in_cm: convertLength(formData.packageDetails.length, units.length),
        breadth_in_cm: convertLength(formData.packageDetails.breadth, units.breadth),
        height_in_cm: convertLength(formData.packageDetails.height, units.height),
        drop_pincode: selectedDrop?.pickup_postal_code,
        pickup_pincode: formData.pickupDetails.pincode,
      }));

      console.log("📦 Fetching Courier Pricing (REVERSE only):", JSON.stringify(requestPayload, null, 2));

      const response = await axios.post(
        apiFetchCourierPricing,
        requestPayload,
        {
          headers: {
            Authorization: authToken,
            "x-merchant-id": merchantId,
            "Content-Type": "application/json",
          },
        }
      );

      // Deduplicate by carrier_partner_public_name: keep first occurrence only
      const seenNames = new Set();
      const formattedOptions = (response.data || [])
        .filter((rate) => {
          const name = rate.carrier_partner_public_name;
          if (seenNames.has(name)) return false;
          seenNames.add(name);
          return true;
        })
        .map((rate) => ({
          carrier_partner_public_name: rate.carrier_partner_public_name,
          total_amount_charged_after_tax: Number(Number(rate.total_amount_charged_after_tax).toFixed(2)),
          max_tat_in_days: rate.max_tat_in_days,
        }));

      setCourierOptions(formattedOptions);
    } catch (error) {
      console.error("❌ Error fetching courier rates:", error);
      toast.error("Failed to fetch courier rates.");
    } finally {
      setLoadingCouriers(false);
    }
  };


  const fetchServiceability = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        toast.error("Missing auth token.");
        return;
      }
      const apiFetchServiceabilty = API.checkServiceability();
      const payload = {
        drop_pincode: selectedDrop?.pickup_postal_code,
        pickup_pincode: formData.pickupDetails.pincode,
      };
      const response = await axios.post(apiFetchServiceabilty, payload, {
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
        },
      });
      if (
        response.data &&
        response.data.merchant_domestic_serviceabilities &&
        response.data.merchant_domestic_serviceabilities.length > 0
      ) {
        const serviceData = response.data.merchant_domestic_serviceabilities
          .filter((p) => /REVERSE/i.test(p.carrier_partner_id || ""))
          .map((partner) => ({
            id: partner.carrier_partner_id,
            name: (partner.carrier_partner_identifier_string || "N/A").trim(),
            prepaid: partner.prepaid_serviceability_details?.is_serviceable ?? false,
            cod: partner.cod_serviceability_details?.is_serviceable ?? false,
          }));
        setCourierServiceability(serviceData);
      } else {
        toast.error("No serviceability data available.");
      }
    } catch (error) {
      console.error("Error fetching serviceability:", error);
      toast.error("Failed to fetch serviceability details.");
    }
  };

  const renderStepContent = () => {
    const currentSection = steps[step - 1]?.key;
    if (!currentSection) return null;

    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="relative">
            <div className="w-24 h-24 rounded-full border-4 border-gray-200 border-t-blue-600 animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Icons.Package />
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-700 mt-6">Processing your order...</p>
          <p className="text-gray-500 mt-2">Please wait while we create your reverse shipment</p>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="space-y-4 animate-fade-in">
          {/* Header Section with Icon */}
          <div className="flex items-center space-x-2.5 pb-3 border-b border-gray-200">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
              <div className="w-4 h-4 text-white">
                <Icons.User />
              </div>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Customer Details</h2>
              <p className="text-xs text-gray-500">Who is sending the return? (Sender's Info)</p>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-4 h-4 text-gray-600">
                <Icons.Phone />
              </div>
              <h3 className="text-sm font-semibold text-gray-800">Contact Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Phone Number */}
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <div className="w-4 h-4 text-gray-400">
                      <Icons.Phone />
                    </div>
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    name="phoneNumber"
                    placeholder="10-digit mobile number"
                    value={formData.pickupDetails.phoneNumber}
                    onChange={handlePhoneNumberChange}
                    className={`w-full pl-10 pr-3 py-2 border-2 text-sm ${
                      errors.phoneNumber 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                    } rounded-lg shadow-sm focus:ring-2 focus:ring-opacity-20 transition-all duration-200 bg-white`}
                  />
                  {formData.pickupDetails.phoneNumber && !errors.phoneNumber && formData.pickupDetails.phoneNumber.length === 10 && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                        <div className="w-3 h-3 text-green-600">
                          <Icons.Check />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {errors.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <span className="mr-1">⚠</span> {errors.phoneNumber}
                  </p>
                )}
              </div>

              {/* Name */}
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <div className="w-4 h-4 text-gray-400">
                      <Icons.User />
                    </div>
                  </div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Sender's full name"
                    value={formData.pickupDetails.name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        pickupDetails: { ...prev.pickupDetails, name: e.target.value },
                      }))
                    }
                    onBlur={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, name: e.target.value.trim() } }))}
                    className={`w-full pl-10 pr-3 py-2 border-2 text-sm ${
                      errors.name 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                    } rounded-lg shadow-sm focus:ring-2 focus:ring-opacity-20 transition-all duration-200 bg-white`}
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <span className="mr-1">⚠</span> {errors.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="relative md:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Email Address <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <div className="w-4 h-4 text-gray-400">
                      <Icons.Mail />
                    </div>
                  </div>
                  <input
                    type="email"
                    name="email"
                    placeholder="sender@example.com"
                    value={formData.pickupDetails.email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        pickupDetails: { ...prev.pickupDetails, email: e.target.value },
                      }))
                    }
                    onBlur={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, email: e.target.value.trim() } }))}
                    className="w-full pl-10 pr-3 py-2 border-2 text-sm border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:ring-opacity-20 rounded-lg shadow-sm transition-all duration-200 bg-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Pickup Address Card */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-4 h-4 text-gray-600">
                <Icons.MapPin />
              </div>
              <h3 className="text-sm font-semibold text-gray-800">Pickup Address (Buyer's Address)</h3>
            </div>

            <div className="space-y-4">
              {/* Complete Address */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Complete Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="address"
                  placeholder="House/Flat No., Building Name, Street, Area, Landmark"
                  value={formData.pickupDetails.address}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      pickupDetails: { ...prev.pickupDetails, address: e.target.value },
                    }))
                  }
                  onBlur={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, address: e.target.value.trim() } }))}
                  rows="2"
                  className={`w-full px-3 py-2 border-2 text-sm ${
                    errors.address 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                  } rounded-lg shadow-sm focus:ring-2 focus:ring-opacity-20 transition-all duration-200 bg-white resize-none`}
                />
                {errors.address && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <span className="mr-1">⚠</span> {errors.address}
                  </p>
                )}
              </div>

              {/* Location Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Pincode */}
                <div className="relative">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    placeholder="6-digit pincode"
                    value={formData.pickupDetails.pincode}
                    onChange={handlePincodeChange}
                    maxLength="6"
                    className={`w-full px-3 py-2 border-2 text-sm ${
                      errors.pincode 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                    } rounded-lg shadow-sm focus:ring-2 focus:ring-opacity-20 transition-all duration-200 bg-white`}
                  />
                  {loadingPincode && (
                    <p className="text-blue-600 text-xs mt-1 flex items-center">
                      <svg className="animate-spin h-3 w-3 mr-1.5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Fetching...
                    </p>
                  )}
                  {errors.pincode && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <span className="mr-1">⚠</span> {errors.pincode}
                    </p>
                  )}
                </div>

                {/* City */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    placeholder="City name"
                    value={formData.pickupDetails.city}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        pickupDetails: { ...prev.pickupDetails, city: e.target.value },
                      }))
                    }
                    onBlur={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, city: e.target.value.trim() } }))}
                    className={`w-full px-3 py-2 border-2 text-sm ${
                      errors.city 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                    } rounded-lg shadow-sm focus:ring-2 focus:ring-opacity-20 transition-all duration-200 bg-white`}
                  />
                  {errors.city && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <span className="mr-1">⚠</span> {errors.city}
                    </p>
                  )}
                </div>

                {/* State */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="state"
                    placeholder="State name"
                    value={formData.pickupDetails.state}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        pickupDetails: { ...prev.pickupDetails, state: e.target.value },
                      }))
                    }
                    onBlur={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, state: e.target.value.trim() } }))}
                    className={`w-full px-3 py-2 border-2 text-sm ${
                      errors.state 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                    } rounded-lg shadow-sm focus:ring-2 focus:ring-opacity-20 transition-all duration-200 bg-white`}
                  />
                  {errors.state && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <span className="mr-1">⚠</span> {errors.state}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Customer Lookup Modal */}
          {showCustomerModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-md animate-fade-in">
              <div className="bg-white w-full max-w-2xl mx-4 rounded-3xl shadow-2xl overflow-hidden transform animate-scale-in">
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Icons.Sparkles />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      Saved Addresses Found
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowCustomerModal(false)}
                    className="p-2 rounded-xl hover:bg-white/10 transition-colors duration-200"
                    aria-label="Close"
                  >
                    <Icons.Close />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="max-h-[70vh] overflow-y-auto p-6 space-y-3">
                  {customerLookupResults.map((cust, i) => (
                    <button
                      key={i}
                      onClick={() => selectCustomer(cust)}
                      className="w-full text-left p-5 rounded-2xl border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all duration-200 group bg-gradient-to-br from-white to-gray-50"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                            {cust.drop_poc}
                          </p>
                          <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                            {cust.drop_address}
                          </p>
                          <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                            <span className="flex items-center">
                              <Icons.MapPin />
                              <span className="ml-1">{cust.drop_city}, {cust.drop_state}</span>
                            </span>
                            <span className="font-semibold">{cust.drop_postal_code}</span>
                          </div>
                        </div>
                        <div className="ml-4 flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 group-hover:bg-blue-600 transition-colors">
                          <svg className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="space-y-6 animate-fade-in">
          {/* Header Section */}
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <Icons.Location />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Drop Location</h2>
                <p className="text-sm text-gray-500 mt-0.5">Select the seller drop location where the order will be delivered</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Add New Address */}
              <button
                onClick={openAddAddress}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                <span className="font-semibold text-sm">Add New</span>
              </button>
              {/* Refresh Button */}
              <button
                onClick={fetchAndStoreDropDetails}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <Icons.Refresh />
                <span className="font-semibold text-sm">Refresh</span>
              </button>
            </div>
          </div>

          {/* Address Management Modal + Delete Confirm */}
          <PickupAddressModal open={addrModalOpen} mode={addrModalMode} location={addrModalLocation} onClose={() => setAddrModalOpen(false)} onSaved={handleAddressSaved} />
          <DeleteConfirmModal open={deleteConfirmOpen} onConfirm={confirmDeleteAddress} onCancel={() => { setDeleteConfirmOpen(false); setPickupToDelete(null); }} deleting={deletingPickup} />

          {/* Drop Locations */}
          {dropDetails.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {dropDetails.map((drop) => {
                const isSelected = selectedDrop?.pickup_address_id === drop.pickup_address_id;
                return (
                  <div
                    key={drop.pickup_address_id}
                    className={`relative p-6 rounded-2xl border-2 transition-all duration-300 cursor-pointer group ${
                      isSelected
                        ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg ring-4 ring-blue-100'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                    onClick={() => setSelectedDrop(drop)}
                  >
                    {/* Selected Badge */}
                    {isSelected && (
                      <div className="absolute -top-3 -right-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                          <Icons.Check />
                        </div>
                      </div>
                    )}

                    <div className="flex items-start justify-between">
                      {/* Location Details */}
                      <div className="flex-1 space-y-3">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{drop.pickup_organisation}</h3>
                          <p className="text-sm text-gray-500 mt-1">Drop ID: {drop.pickup_address_id}</p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-start space-x-3">
                            <Icons.MapPin />
                            <p className="text-gray-700 leading-relaxed">
                              {drop.pickup_address}, {drop.pickup_city}, {drop.pickup_district}, {drop.pickup_state} - <span className="font-semibold">{drop.pickup_postal_code}</span>
                            </p>
                          </div>

                          <div className="flex items-center space-x-3">
                            <Icons.User />
                            <p className="text-gray-700">{drop.pickup_poc}</p>
                          </div>

                          <div className="flex items-center space-x-3">
                            <Icons.Phone />
                            <p className="text-gray-700">{drop.pickup_poc_contact_number}</p>
                          </div>

                          {drop.pickup_email && (
                            <div className="flex items-center space-x-3">
                              <Icons.Mail />
                              <p className="text-gray-700">{drop.pickup_email}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="ml-4 flex flex-col items-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedDrop(drop); }}
                          className={`px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 text-sm ${
                            isSelected
                              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {isSelected ? (
                            <span className="flex items-center space-x-2">
                              <Icons.Check />
                              <span>Selected</span>
                            </span>
                          ) : (
                            'Select'
                          )}
                        </button>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditAddress(drop); }}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-all flex items-center gap-1"
                            title="Edit this address"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            Edit
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openDeleteAddress(drop.pickup_address_id); }}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-all flex items-center gap-1"
                            title="Delete this address"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </div>
              <p className="text-gray-500 text-lg mb-3">No drop locations found</p>
              <p className="text-gray-400 text-sm mb-4">Add a new address to get started</p>
              <button
                onClick={openAddAddress}
                className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all font-semibold flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                Add Drop Location
              </button>
            </div>
          )}
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="space-y-6 animate-fade-in">
          {/* Header Section */}
          <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg">
              <Icons.Receipt />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Order Details</h2>
              <p className="text-sm text-gray-500 mt-0.5">Configure payment and item information</p>
            </div>
          </div>

          {/* Payment Mode Card */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-sm border border-gray-100">
            <label className="block text-lg font-semibold text-gray-800 mb-4">Payment Mode</label>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-50 text-green-700 border-2 border-green-300">
              <Icons.Check />
              <span className="ml-2 font-semibold">PREPAID (Reverse shipments only)</span>
            </div>
          </div>

          {/* Invoice & Order ID Card */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Invoice Value */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Invoice Value <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
                  <input
                    type="number"
                    value={formData.orderDetails.invoiceValue}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        orderDetails: {
                          ...prev.orderDetails,
                          invoiceValue: e.target.value,
                        },
                      }))
                    }
                    className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-xl shadow-sm transition-all duration-200 bg-white"
                  />
                </div>
                {errors.invoiceValue && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠</span> {errors.invoiceValue}
                  </p>
                )}
              </div>

              {/* Client Order ID */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Client Order ID <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.orderDetails.clientOrderId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        orderDetails: { ...prev.orderDetails, clientOrderId: e.target.value },
                      }))
                    }
                    onBlur={(e) => setFormData((prev) => ({ ...prev, orderDetails: { ...prev.orderDetails, clientOrderId: e.target.value.trim() } }))}
                    className="w-full px-4 py-3.5 pr-24 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-xl shadow-sm transition-all duration-200"
                    placeholder="Enter or generate ID"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateOrderId}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg text-sm font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <Icons.Sparkles />
                  </button>
                </div>
                {errors.clientOrderId && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <span className="mr-1">⚠</span> {errors.clientOrderId}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Item Details Card */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Items in Order</h3>
              <button
                onClick={addItemDetail}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl text-sm font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <span className="text-lg">+</span>
                <span>Add Item</span>
              </button>
            </div>

            <div className="space-y-4">
              {formData.orderDetails.itemDetails.map((item, index) => (
                <div key={index} className="p-5 bg-white rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-200 shadow-sm">
                  <div className="flex items-start justify-between mb-4">
                    <h4 className="text-sm font-semibold text-gray-600">Item #{index + 1}</h4>
                    {formData.orderDetails.itemDetails.length > 1 && (
                      <button
                        onClick={() => removeItemDetail(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      >
                        <Icons.Close />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {/* SKU ID */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        SKU ID {!cloneMode && <span className="text-red-500">*</span>}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={item.skuId}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData((prev) => {
                              const items = [...prev.orderDetails.itemDetails];
                              items[index] = { ...items[index], skuId: val };
                              return { ...prev, orderDetails: { ...prev.orderDetails, itemDetails: items } };
                            });
                          }}
                          onBlur={(e) => {
                            const trimmed = e.target.value.trim();
                            setFormData((prev) => {
                              const items = [...prev.orderDetails.itemDetails];
                              items[index] = { ...items[index], skuId: trimmed };
                              return { ...prev, orderDetails: { ...prev.orderDetails, itemDetails: items } };
                            });
                          }}
                          className="w-full px-4 py-3 pr-24 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-xl shadow-sm transition-all duration-200"
                          placeholder="Enter SKU"
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => fetchSKUDetails(index, item.skuId)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                            title="Search SKU"
                          >
                            <Icons.Search />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const sku = `SKU-${uuidv4().slice(0, 8).toUpperCase()}`;
                              setFormData((prev) => {
                                const items = [...prev.orderDetails.itemDetails];
                                items[index] = { ...items[index], skuId: sku };
                                return { ...prev, orderDetails: { ...prev.orderDetails, itemDetails: items } };
                              });
                            }}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                            title="Generate SKU"
                          >
                            <Icons.Sparkles />
                          </button>
                        </div>
                      </div>
                      {errors[`skuId-${index}`] && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                          <span className="mr-1">⚠</span> {errors[`skuId-${index}`]}
                        </p>
                      )}
                    </div>

                    {/* Item Name */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Item Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => handleItemChange(index, "name", e.target.value)}
                        onBlur={(e) => handleItemChange(index, "name", e.target.value.trim())}
                        className="w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-xl shadow-sm transition-all duration-200"
                        placeholder="Product name"
                      />
                      {errors[`name-${index}`] && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                          <span className="mr-1">⚠</span> {errors[`name-${index}`]}
                        </p>
                      )}
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Qty <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-xl shadow-sm transition-all duration-200"
                        placeholder="1"
                        min="1"
                      />
                      {errors[`quantity-${index}`] && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                          <span className="mr-1">⚠</span> {errors[`quantity-${index}`]}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Unit Price (Full Width Below) */}
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Unit Price <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                        className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-xl shadow-sm transition-all duration-200"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    {errors[`unitPrice-${index}`] && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <span className="mr-1">⚠</span> {errors[`unitPrice-${index}`]}
                      </p>
                    )}
                  </div>

                  {/* Item Total + HSN Code */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600">Item Total</span>
                        <button
                          type="button"
                          onClick={() => setExpandedHSNItems(prev => ({ ...prev, [index]: !prev[index] }))}
                          className="px-2 py-0.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-all flex items-center gap-1"
                        >
                          <span className="text-sm">{expandedHSNItems[index] ? "−" : "+"}</span>
                          HSN Code
                        </button>
                      </div>
                      <span className="text-lg font-bold text-gray-900">
                        ₹{((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)).toFixed(2)}
                      </span>
                    </div>
                    {expandedHSNItems[index] && (
                      <div className="mt-3 animate-fade-in">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">HSN Code</label>
                        <input
                          type="text"
                          value={item.hsnCode || ""}
                          onChange={(e) => handleItemChange(index, "hsnCode", e.target.value)}
                          onBlur={(e) => handleItemChange(index, "hsnCode", e.target.value.trim())}
                          className="w-full px-3 py-2 border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 rounded-lg text-sm transition-all"
                          placeholder="e.g. 61091000"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Order Total */}
            <div className="mt-6 p-5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg">
              <div className="flex justify-between items-center">
                <span className="text-white text-lg font-semibold">Order Total</span>
                <span className="text-white text-2xl font-bold">
                  ₹{Number(formData.orderDetails.invoiceValue || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Optional Fields Toggle Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowHSNDetails((s) => !s)}
              className="flex items-center space-x-2 px-4 py-2 border-2 border-gray-200 hover:border-blue-500 rounded-xl text-sm font-semibold text-gray-700 hover:text-blue-600 transition-all duration-200"
            >
              <span>{showHSNDetails ? "−" : "+"}</span>
              <span>HSN Code</span>
            </button>
            <button
              onClick={() => setShowShippingDetails((s) => !s)}
              className="flex items-center space-x-2 px-4 py-2 border-2 border-gray-200 hover:border-blue-500 rounded-xl text-sm font-semibold text-gray-700 hover:text-blue-600 transition-all duration-200"
            >
              <span>{showShippingDetails ? "−" : "+"}</span>
              <span>Invoice Number</span>
            </button>
          </div>

          {/* HSN Details */}
          {showHSNDetails && (
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-sm border border-gray-100 animate-fade-in">
              <label className="block text-sm font-semibold text-gray-700 mb-2">HSN Code</label>
              <input
                type="text"
                value={formData.orderDetails.hsn || ""}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    orderDetails: { ...p.orderDetails, hsn: e.target.value },
                  }))
                }
                onBlur={(e) => setFormData((p) => ({ ...p, orderDetails: { ...p.orderDetails, hsn: e.target.value.trim() } }))}
                className="w-full px-4 py-3.5 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-xl shadow-sm transition-all duration-200"
                placeholder="Enter HSN code"
              />
            </div>
          )}

          {/* Invoice Number */}
          {showShippingDetails && (
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-sm border border-gray-100 animate-fade-in">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Invoice Number</label>
              <input
                type="text"
                value={formData.orderDetails.invoiceNumber || ""}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    orderDetails: { ...p.orderDetails, invoiceNumber: e.target.value },
                  }))
                }
                onBlur={(e) => setFormData((p) => ({ ...p, orderDetails: { ...p.orderDetails, invoiceNumber: e.target.value.trim() } }))}
                className="w-full px-4 py-3.5 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-xl shadow-sm transition-all duration-200"
                placeholder="Enter invoice number"
              />
            </div>
          )}
        </div>
      );
    }

    if (step === 4) {
      return (
        <div className="space-y-6 animate-fade-in">
          {/* Header Section */}
          <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
              <Icons.Package />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Package Details</h2>
              <p className="text-sm text-gray-500 mt-0.5">Specify dimensions and weight for accurate shipping</p>
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 rounded-xl">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-blue-900">Pro Tip</p>
                <p className="text-xs text-blue-800 mt-1">
                  Accurate dimensions ensure correct shipping charges. Measure the outer packaging dimensions.
                </p>
              </div>
            </div>
          </div>

          {/* Package Dimensions Card */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { key: 'weight', label: 'Weight', placeholder: '500', units: ['gm', 'kg'], icon: '⚖️' },
                { key: 'length', label: 'Length', placeholder: '30', units: ['cm', 'inch'], icon: '↔️' },
                { key: 'breadth', label: 'Breadth', placeholder: '20', units: ['cm', 'inch'], icon: '↕️' },
                { key: 'height', label: 'Height', placeholder: '10', units: ['cm', 'inch'], icon: '↕️' },
              ].map(({ key, label, placeholder, units: unitOptions, icon }) => (
                <div key={key} className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    <span className="mr-2">{icon}</span>
                    {label}
                  </label>
                  <div className="flex items-stretch space-x-2">
                    <input
                      type="number"
                      placeholder={placeholder}
                      value={formData.packageDetails[key]}
                      onChange={e => handlePackageValueChange(key, e.target.value)}
                      className="flex-1 px-3 py-2.5 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-lg shadow-sm transition-all duration-200 bg-white text-sm"
                    />
                    <select
                      value={units[key]}
                      onChange={e => handlePackageUnitChange(key, e.target.value)}
                      className="px-2 py-2.5 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-lg shadow-sm transition-all duration-200 bg-white font-semibold text-gray-700 text-sm min-w-[60px]"
                    >
                      {unitOptions.map(u => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>

            {/* Volumetric Weight Info */}
            {formData.packageDetails.length && formData.packageDetails.breadth && formData.packageDetails.height && (
              <div className="mt-4 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-indigo-900">Volumetric Weight</p>
                    <p className="text-xs text-indigo-700 mt-0.5">
                      (L × B × H) / 5000
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-indigo-900">
                      {(
                        (convertLength(formData.packageDetails.length, units.length) *
                          convertLength(formData.packageDetails.breadth, units.breadth) *
                          convertLength(formData.packageDetails.height, units.height)) /
                        5000
                      ).toFixed(2)}
                      <span className="text-xs ml-1">kg</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Package Preview Visualization */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 shadow-sm border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Package Preview</h3>
            <div className="flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg min-h-[160px]">
              <div className="relative">
                {/* 3D Box Illustration */}
                <div
                  className="relative bg-gradient-to-br from-amber-100 to-orange-100 border-4 border-amber-300 rounded-lg shadow-2xl transform rotate-3 transition-all duration-300 hover:rotate-0"
                  style={{
                    width: `${Math.min(150, Math.max(80, Number(formData.packageDetails.length) || 100))}px`,
                    height: `${Math.min(150, Math.max(60, Number(formData.packageDetails.height) || 80))}px`,
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icons.Package />
                  </div>
                  {/* Dimension Labels */}
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-semibold text-gray-600 whitespace-nowrap">
                    {formData.packageDetails.length || '?'} {units.length}
                  </div>
                  <div className="absolute -right-12 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-600 whitespace-nowrap">
                    {formData.packageDetails.height || '?'} {units.height}
                  </div>
                  <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-semibold text-gray-600 whitespace-nowrap">
                    {formData.packageDetails.weight || '?'} {units.weight}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (step === 6 && responseData) {
      return (
        <div className="space-y-8 animate-fade-in">
          {/* Success Animation */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 shadow-2xl mb-4 animate-bounce-slow">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Reverse Order Created Successfully!</h2>
            <p className="text-lg text-gray-600">
              Thank you, <span className="font-semibold text-blue-600">{responseData.username}</span>
            </p>
          </div>

          {/* Order Details Card */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center space-x-3 mb-4 pb-4 border-b border-gray-200">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Icons.Receipt />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Reverse Order Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Shipment Order ID</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">{responseData.shipment_order_id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Order Reference</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">{responseData.order_reference_number}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Waybill Number</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">{responseData.waybill}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Merchant ID</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">{responseData.merchant_id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Courier Partner</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Icons.Truck />
                    <p className="text-lg font-semibold text-blue-600">{responseData.courier_name}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {responseData.label_url && (
              <a
                href={responseData.label_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span>Shipping Label</span>
              </a>
            )}
            {responseData.commercial_invoice_url && (
              <a
                href={responseData.commercial_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                <Icons.Receipt />
                <span>Invoice</span>
              </a>
            )}
            <button
              onClick={() => navigate("/all-shipments")}
              className="flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>Dashboard</span>
            </button>
          </div>

          {/* Celebration Effect */}
          <div className="text-center text-6xl animate-pulse">
            🎉 🎊 ✨
          </div>
        </div>
      );
    }


    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header Section */}
        <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
            <Icons.Truck />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Select Courier</h2>
            <p className="text-sm text-gray-500 mt-0.5">Choose your preferred delivery partner</p>
          </div>
        </div>

        {/* Courier Selection Mode */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-sm border border-gray-100">
          <label className="block text-lg font-semibold text-gray-800 mb-4">Selection Mode</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="relative cursor-pointer group">
              <input
                type="radio"
                name="courierOption"
                checked={formData.courierDetails.useRecommendation}
                onChange={() => handleCourierSelection(true)}
                className="hidden"
              />
              <div
                className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                  formData.courierDetails.useRecommendation
                    ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-md ring-4 ring-blue-100'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div
                    className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      formData.courierDetails.useRecommendation ? 'border-blue-600' : 'border-gray-300'
                    }`}
                  >
                    {formData.courierDetails.useRecommendation && (
                      <div className="w-3 h-3 bg-blue-600 rounded-full" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <Icons.Lightning />
                      <span className={`text-lg font-semibold ${formData.courierDetails.useRecommendation ? 'text-blue-600' : 'text-gray-700'}`}>
                        Smart Allocation
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Use intelligent routing rules for optimal delivery
                    </p>
                  </div>
                </div>
              </div>
            </label>

            <label className="relative cursor-pointer group">
              <input
                type="radio"
                name="courierOption"
                checked={!formData.courierDetails.useRecommendation}
                onChange={() => handleCourierSelection(false)}
                className="hidden"
              />
              <div
                className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                  !formData.courierDetails.useRecommendation
                    ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-md ring-4 ring-purple-100'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div
                    className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      !formData.courierDetails.useRecommendation ? 'border-purple-600' : 'border-gray-300'
                    }`}
                  >
                    {!formData.courierDetails.useRecommendation && (
                      <div className="w-3 h-3 bg-purple-600 rounded-full" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <Icons.User />
                      <span className={`text-lg font-semibold ${!formData.courierDetails.useRecommendation ? 'text-purple-600' : 'text-gray-700'}`}>
                        Manual Selection
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Choose a specific courier from available options
                    </p>
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Courier Options - Only show if manual selection */}
        {!formData.courierDetails.useRecommendation && (
          <div className="space-y-4 animate-fade-in">
            {/* Loading State */}
            {loadingCouriers ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mb-4">
                  <svg className="animate-spin h-10 w-10 text-blue-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
                <p className="text-gray-600 text-lg font-semibold">Fetching courier rates...</p>
              </div>
            ) : courierOptions.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {courierOptions.map((option) => {
                  const today = new Date();
                  const deliveryDate = new Date(today);
                  deliveryDate.setDate(today.getDate() + option.max_tat_in_days);
                  const serviceableEntries = courierServiceability.filter(
                    (s) => s.name === (option.carrier_partner_public_name || "").trim()
                  );
                  const isServiceable = serviceableEntries.some((s) => s.prepaid);
                  const isSelected = formData.courierDetails.courierPartner === option.carrier_partner_public_name;

                  return (
                    <div
                      key={option.carrier_partner_public_name}
                      className={`p-5 rounded-2xl border-2 transition-all duration-200 cursor-pointer ${
                        isSelected
                          ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg ring-4 ring-blue-100'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-md bg-white'
                      }`}
                      onClick={() => {
                        if (isServiceable) {
                          setFormData((prev) => ({
                            ...prev,
                            courierDetails: {
                              ...prev.courierDetails,
                              courierPartner: option.carrier_partner_public_name,
                            },
                          }));
                        }
                      }}
                    >
                      <div className="flex items-center justify-between gap-4">
                        {/* Courier Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className={`w-10 h-10 rounded-lg ${isSelected ? 'bg-blue-600' : 'bg-gray-100'} flex items-center justify-center flex-shrink-0`}>
                              <Icons.Truck />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-base font-bold text-gray-900 truncate">{option.carrier_partner_public_name}</h3>
                              <div className="flex items-center space-x-2 mt-1">
                                {isServiceable ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                    <Icons.Check />
                                    <span className="ml-1">Serviceable</span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                    ✗ Not Serviceable
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Courier Details Grid */}
                          <div className="grid grid-cols-3 gap-3">
                            <div className="text-center p-2 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg">
                              <p className="text-lg font-bold text-blue-600">₹{Number(option.total_amount_charged_after_tax).toFixed(2)}</p>
                              <p className="text-xs text-gray-600 mt-0.5">Total Cost</p>
                            </div>
                            <div className="text-center p-2 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg">
                              <p className="text-lg font-bold text-purple-600">{option.max_tat_in_days}</p>
                              <p className="text-xs text-gray-600 mt-0.5">Days</p>
                            </div>
                            <div className="text-center p-2 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg">
                              <p className="text-sm font-bold text-green-600">{deliveryDate.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</p>
                              <p className="text-xs text-gray-600 mt-0.5">Delivery</p>
                            </div>
                          </div>
                        </div>

                        {/* Select Button */}
                        <div className="flex-shrink-0">
                          <button
                            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 whitespace-nowrap ${
                              isSelected
                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                                : isServiceable
                                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                            }`}
                            disabled={!isServiceable}
                          >
                            {isSelected ? (
                              <span className="flex items-center space-x-2">
                                <Icons.Check />
                                <span>Selected</span>
                              </span>
                            ) : (
                              'Select'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Icons.Truck />
                </div>
                <p className="text-gray-500 text-lg">No courier options available</p>
              </div>
            )}
          </div>
        )}

        {/* Info Message for Smart Allocation */}
        {formData.courierDetails.useRecommendation && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-5 rounded-xl animate-fade-in">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icons.Lightning />
              </div>
              <div>
                <p className="text-sm font-semibold text-blue-900">Smart Allocation Enabled</p>
                <p className="text-sm text-blue-800 mt-1">
                  Our system will automatically select the best courier based on your allocation rules, pricing, and delivery time.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleItemChange = (index, key, value) => {
    const updatedItems = [...formData.orderDetails.itemDetails];
    updatedItems[index] = { ...updatedItems[index], [key]: value };
    setFormData((prev) => ({
      ...prev,
      orderDetails: {
        ...prev.orderDetails,
        itemDetails: updatedItems,
      },
    }));
  };

  const removeItemDetail = (index) => {
    if (formData.orderDetails.itemDetails.length > 1) {
      const filteredItems = formData.orderDetails.itemDetails.filter((_, idx) => idx !== index);
      setFormData({
        ...formData,
        orderDetails: {
          ...formData.orderDetails,
          itemDetails: filteredItems
        }
      });
    } else {
      toast.error("At least one item must remain.");
    }
  };

  const addItemDetail = () => {
    const newItem = {
      name: "",
      description: "",
      quantity: 1,
      unitPrice: "",
      skuId: "",
      hsnCode: "",
    };
    setFormData({
      ...formData,
      orderDetails: {
        ...formData.orderDetails,
        itemDetails: [...formData.orderDetails.itemDetails, newItem]
      }
    });
  };

  const buildApiPayload = (trimmedForm) => {
    return {
      shipment_order_type: "RVP",
      process_shipment_order_in_async: false,
      pickup_details: {
        pickup_address: trimmedForm.pickupDetails.address,
        pickup_city: trimmedForm.pickupDetails.city,
        pickup_state: trimmedForm.pickupDetails.state,
        pickup_postal_code: trimmedForm.pickupDetails.pincode,
        pickup_poc: trimmedForm.pickupDetails.name,
        pickup_country_code: "IN",
        pickup_poc_contact_number: trimmedForm.pickupDetails.phoneNumber,
        pickup_poc_email: trimmedForm.pickupDetails.email || "abc@gmail.com",
      },
      drop_details: {
        drop_poc: selectedDrop.pickup_poc,
        drop_address: selectedDrop.pickup_address,
        drop_city: selectedDrop.pickup_city,
        drop_country_code: "IN",
        drop_poc_contact_number: selectedDrop.pickup_poc_contact_number,
        drop_poc_email: selectedDrop.pickup_poc_email || "abc@gmail.com",
        drop_state: selectedDrop.pickup_state,
        drop_postal_code: selectedDrop.pickup_postal_code,
      },
      reverse_pickup_details: {
        reverse_pickup_reason: "reverse order",
      },
      shipment_details: {
        ...(!trimmedForm.courierDetails.useRecommendation && trimmedForm.courierDetails.courierPartner && {
          shipment_carrier_details: {
            carrier_partner_public_name: trimmedForm.courierDetails.courierPartner,
          },
        }),
        order_details: {
          order_type: "PREPAID",
          order_reference_number: trimmedForm.orderDetails.clientOrderId,
          order_invoice_details: {
            invoice_value: parseFloat(trimmedForm.orderDetails.invoiceValue),
            invoice_number: trimmedForm.orderDetails.invoiceNumber || null,
            invoice_currency: "INR"
          },
        },
        shipment_packages: [
          {
            items: trimmedForm.orderDetails.itemDetails.map(item => ({
              item_general_details: {
                item_name: item.name,
                sku: item.skuId
              },
              quantity: item.quantity,
              item_payment_details: {
                unit_price: item.unitPrice
              }
            })),
            dimensions: {
              length: convertLength(trimmedForm.packageDetails.length, units.length),
              breadth: convertLength(trimmedForm.packageDetails.breadth, units.breadth),
              height: convertLength(trimmedForm.packageDetails.height, units.height),
              unit: 'cm'
            },
            weight: {
              value: convertWeight(trimmedForm.packageDetails.weight, units.weight),
              unit: "gm"
            }
          }
        ]
      }
    };
  };

  const handleSaveAsDraft = async () => {
    const trimmedForm = deepTrimStrings(formData);
    setFormData(trimmedForm);

    const orderData = buildApiPayload(trimmedForm);

    setSavingDraft(true);
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      const merchantId = localStorage.getItem("merchantId");
      const draftPayload = {
        order_data: orderData,
        order_source: "UI_PANEL",
        ...(trimmedForm.orderDetails.clientOrderId && {
          title: `Reverse Order ${trimmedForm.orderDetails.clientOrderId}`
        }),
      };

      const apiUrl = draftOrderId
        ? API.updateDraftOrder(draftOrderId)
        : API.createDraftOrder();

      const response = draftOrderId
        ? await axios.put(apiUrl, draftPayload, {
            headers: {
              'Authorization': `${authToken}`,
              'Content-Type': 'application/json',
              'merchantId': merchantId
            }
          })
        : await axios.post(apiUrl, draftPayload, {
            headers: {
              'Authorization': `${authToken}`,
              'Content-Type': 'application/json',
              'merchantId': merchantId
            }
          });

      if (response.data.metadata?.success !== false && response.data.draft_order_id) {
        const newDraftId = response.data.draft_order_id;
        // Store the draftOrderId so subsequent saves use update API (not create)
        if (!draftOrderId) setDraftOrderId(newDraftId);
        setDraftSuccessModal({ open: true, draftId: newDraftId, isUpdate: Boolean(draftOrderId) });
      } else if (response.data.metadata?.success) {
        const newDraftId = draftOrderId || response.data.draft_order_id;
        if (!draftOrderId && newDraftId) setDraftOrderId(newDraftId);
        setDraftSuccessModal({ open: true, draftId: newDraftId, isUpdate: Boolean(draftOrderId) });
      } else {
        toast.error(response.data.metadata?.message || "Failed to save draft.");
      }
    } catch (error) {
      const errMsg = error?.response?.data?.metadata?.message || "Error saving draft. Please try again.";
      toast.error(errMsg);
      console.error('Draft save error:', error);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    // Onboarding gate: keep the reverse-order form fully usable, but placing the order requires an
    // activated account. If locked, open the guided onboarding overlay (order data is preserved).
    if (!requireBusinessOps('create your reverse order')) return;

    const trimmedForm = deepTrimStrings(formData);
    setFormData(trimmedForm);

    const apiPayload = buildApiPayload(trimmedForm);

    setLoading(true);
    setResponseData(null);

    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        console.error("Auth token not found in local storage.");
        navigate("/login");
        return;
      }


      const merchantId = localStorage.getItem("merchantId");
      console.log('merchant ID:', merchantId);
      const apiUrl = API.createOrder();

      console.log('API URL:', apiUrl);
      console.log('API Payload:', apiPayload);

      const response = await axios.post(apiUrl, apiPayload, {
        headers: {
          'Authorization': `${authToken}`,
          'Content-Type': 'application/json',
          'x-client-id': merchantId
        }
      });

      const outcome = interpretOrderCreationResponse(response.data);
      if (outcome.kind === "success") {
        setResponseData(outcome.result);
        setStep(6);
      } else {
        setOrderIssue(outcome.issue);
      }
    } catch (error) {
      setOrderIssue(interpretOrderCreationError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOrderIssueAction = (action) => {
    setOrderIssue(null);
    if (action === "recharge") navigate("/wallet?recharge=1");
    else if (action === "wallet") navigate("/wallet");
    else if (action === "orders") navigate("/orders");
    else if (action === "login") navigate("/login");
    else if (action === "retry") handleSubmit();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 mt-2">
      <div className="max-w-7xl mx-auto px-2 lg:px-3">
        <div className="max-w-7xl mx-auto">
          {/* Main Card */}
          <div className="bg-white/80 backdrop-blur-xl shadow-xl rounded-2xl overflow-hidden border border-gray-200">
            {/* Enhanced Step Progress Indicator */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 pt-4 pb-2 border-b border-gray-200">
              <div className="flex justify-between items-center max-w-5xl mx-auto">
                {steps.map((stepItem, index) => {
                  const Icon = stepItem.icon;
                  const stepNumber = index + 1;
                  const isActive = step === stepNumber;
                  const isCompleted = step > stepNumber;
                  const isReachable = stepNumber <= highestStepReached;
                  const isClickable = isReachable && !isActive;

                  return (
                    <div key={index} className="flex-1 relative">
                      {/* Connecting Line */}
                      {index < steps.length - 1 && (
                        <div className="absolute top-7 left-1/2 w-full h-1 -z-10">
                          <div
                            className={`h-full transition-all duration-500 ${
                              isCompleted ? 'bg-gradient-to-r from-blue-500 to-indigo-500' : 'bg-gray-200'
                            }`}
                          />
                        </div>
                      )}

                      {/* Step Circle */}
                      <button
                        onClick={() => isClickable && handleStepClick(stepNumber)}
                        disabled={!isClickable}
                        title={isActive ? "Current step" : isClickable ? `Go to ${stepItem.label}` : "Complete previous steps first"}
                        className={`relative mx-auto flex flex-col items-center transition-all duration-300 ${
                          isClickable ? 'cursor-pointer hover:scale-105' : isActive ? 'cursor-default' : 'cursor-not-allowed opacity-60'
                        }`}
                      >
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                            isActive
                              ? 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg ring-2 ring-blue-200'
                              : isCompleted
                              ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-md'
                              : isReachable
                              ? 'bg-white border-2 border-blue-300 shadow-sm'
                              : 'bg-white border-2 border-gray-300'
                          }`}
                        >
                          {isCompleted ? (
                            <div className="w-4 h-4 text-white">
                              <Icons.Check />
                            </div>
                          ) : (
                            <div className={`w-4 h-4 ${isActive ? 'text-white' : isReachable ? 'text-blue-500' : 'text-gray-400'}`}>
                              <Icon />
                            </div>
                          )}
                        </div>
                        <span
                          className={`mt-1.5 text-xs font-medium transition-colors duration-300 ${
                            isActive
                              ? 'text-blue-600'
                              : isCompleted
                              ? 'text-green-600'
                              : isReachable
                              ? 'text-blue-500'
                              : 'text-gray-500'
                          }`}
                        >
                          {stepItem.label}
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Progress Bar */}
              <div className="mt-4 max-w-5xl mx-auto">
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 transition-all duration-500 ease-out"
                    style={{ width: `${calculateCompletionPercentage()}%` }}
                  />
                </div>
                <p className="text-center text-xs text-gray-600 mt-1.5">
                  Step {step} of {steps.length} · {calculateCompletionPercentage()}% Complete
                </p>
              </div>
            </div>

            {/* AWB Search Section - Only show on step 1 */}
            {step === 1 && (
              <div className="px-5 pt-5 pb-2 bg-gradient-to-r from-gray-50 to-gray-100">
                <div className="flex items-center gap-3 max-w-5xl mx-auto">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Enter AWB Number (optional)"
                      value={awbNumber}
                      onChange={e => setAwbNumber(e.target.value)}
                      onBlur={e => setAwbNumber(e.target.value.trim())}
                      className="w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 focus:ring-opacity-20 rounded-xl shadow-sm transition-all duration-200 bg-white text-sm"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <Icons.Search />
                    </div>
                  </div>
                  <button
                    onClick={searchOrder}
                    disabled={loading}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Searching...</span>
                      </>
                    ) : (
                      <>
                        <Icons.Search />
                        <span>Search AWB</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step Content */}
            <div className="p-5">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full border-4 border-gray-200 border-t-blue-600 animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icons.Package />
                    </div>
                  </div>
                  <p className="text-xl font-semibold text-gray-700 mt-6">Processing your order...</p>
                  <p className="text-gray-500 mt-2">Please wait while we create your reverse shipment</p>
                </div>
              ) : (
                renderStepContent()
              )}
            </div>

            {/* Navigation Buttons */}
            {!loading && step !== 6 && (
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-5 py-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <button
                    disabled={step === 1}
                    onClick={handleBack}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      step === 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md hover:shadow-lg border-2 border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Back</span>
                  </button>

                  {step === 5 ? (
                    <div className="flex items-center space-x-3">
                      {/* Save as Draft Button */}
                      <button
                        onClick={handleSaveAsDraft}
                        disabled={loading || savingDraft}
                        className={`flex items-center space-x-2 px-5 py-2 rounded-lg font-medium text-sm transition-all duration-200 transform ${
                          loading || savingDraft
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-white text-amber-700 border-2 border-amber-400 hover:bg-amber-50 hover:border-amber-500 shadow-md hover:shadow-lg hover:scale-105'
                        }`}
                      >
                        {savingDraft ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                            </svg>
                            <span>{draftOrderId ? "Update Draft" : "Save as Draft"}</span>
                          </>
                        )}
                      </button>

                      {/* Create Order Button */}
                      <button
                        onClick={handleSubmit}
                        disabled={loading || savingDraft}
                        className={`flex items-center space-x-2 px-5 py-2 rounded-lg font-medium text-sm transition-all duration-200 transform ${
                          loading || savingDraft
                            ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl hover:scale-105'
                        }`}
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>
                            <span>Create Order</span>
                            <div className="w-4 h-4">
                              <Icons.Check />
                            </div>
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleNext}
                      className="flex items-center space-x-2 px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-medium text-sm shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                    >
                      <span>Next</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Draft Saved Success Modal */}
      {draftSuccessModal.open && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {draftSuccessModal.isUpdate ? "Draft Updated!" : "Draft Saved!"}
            </h3>
            <p className="text-gray-600 mb-1">
              {draftSuccessModal.isUpdate
                ? "Your reverse order draft has been updated successfully."
                : "Your reverse order has been saved as a draft."}
            </p>
            <p className="text-xs text-gray-400 font-mono mb-6">{draftSuccessModal.draftId}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate("/draft-orders")}
                className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Go to Draft Orders
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate("/orders")}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                  Orders
                </button>
                <button
                  onClick={() => {
                    setDraftSuccessModal({ open: false, draftId: null, isUpdate: false });
                    navigate("/create-reverse-order", { replace: true, state: {} });
                    window.location.reload();
                  }}
                  className="flex-1 px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  New Order
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      <OrderCreationIssueModal
        issue={orderIssue}
        onClose={() => setOrderIssue(null)}
        onAction={handleOrderIssueAction}
      />
    </div>
  );


};

export default CreateReverseOrder;